import { useEffect, useState } from "react";
import type { GalleryItem } from "@/types/content";

interface PhotoGalleryProps {
  items: GalleryItem[];
  columns?: "default" | "dense";
}

export default function PhotoGallery({ items, columns = "default" }: PhotoGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (openIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowRight") setOpenIndex((i) => (i === null ? i : (i + 1) % items.length));
      if (e.key === "ArrowLeft") setOpenIndex((i) => (i === null ? i : (i - 1 + items.length) % items.length));
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, items.length]);

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg bg-background-100">
        <i className="ri-image-line text-3xl text-foreground-400 mb-2"></i>
        <p className="text-sm text-foreground-500">暂无写真图片</p>
      </div>
    );
  }

  const current = openIndex === null ? null : items[openIndex];
  const gridClass =
    columns === "dense"
      ? "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3"
      : "grid grid-cols-2 md:grid-cols-3 gap-3";

  return (
    <>
      <div className={gridClass}>
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setOpenIndex(i)}
            className="group relative block aspect-[3/4] rounded-lg overflow-hidden bg-background-100 cursor-pointer"
          >
            <img
              src={item.image}
              alt={`${item.streamerName} ${item.theme}写真`}
              title={`${item.streamerName} 韩国女主播写真`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-colors">
              <i className="ri-zoom-in-line text-background-50 text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </span>
            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[11px] bg-background-50/90 text-foreground-800">
              {item.theme}
            </span>
          </button>
        ))}
      </div>

      {current && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/90 p-4 animate-fade-in"
          onClick={() => setOpenIndex(null)}
        >
          <button
            type="button"
            aria-label="关闭预览"
            onClick={() => setOpenIndex(null)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/40 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {items.length > 1 && (
            <>
              <button
                type="button"
                aria-label="上一张"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenIndex((openIndex! - 1 + items.length) % items.length);
                }}
                className="absolute left-3 md:left-8 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/40 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenIndex((openIndex! + 1) % items.length);
                }}
                className="absolute right-3 md:right-8 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/40 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            </>
          )}

          <figure className="max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={current.image}
              alt={`${current.streamerName} ${current.theme}写真`}
              title={`${current.streamerName} 韩国女主播写真`}
              className="max-w-full max-h-[78vh] object-contain rounded-lg"
            />
            <figcaption className="mt-3 text-sm text-background-200 text-center">
              {current.streamerName} · {current.theme} · {(openIndex ?? 0) + 1} / {items.length}
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
}